import * as Popover from '@radix-ui/react-popover';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import React, { type FormEvent, useCallback, useState, useRef, useEffect } from 'react';
import { useAutocompleteSuggestions } from '#app/hooks/useAutocompleteSuggestions.ts';
import { cn } from '#app/utils/misc.tsx';
import { Input } from './input';

export interface InputAddressProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onPlaceSelect: (place: google.maps.places.Place | null) => void;
}

/**
 * InputAddress - A Google Places autocomplete input component using Radix UI Popover
 *
 * Features:
 * - Accessible dropdown with keyboard navigation (Arrow keys, Enter, Escape)
 * - Smooth animations using Tailwind CSS
 * - Proper ARIA attributes for screen readers
 * - Auto-positioning to prevent overflow
 * - Session token management for Google Places API
 *
 * @example
 * ```tsx
 * import { APIProvider } from "@vis.gl/react-google-maps"
 * import { InputAddress } from "#app/components/ui/input-address"
 *
 * function MyForm() {
 *   const handlePlaceSelect = (place: google.maps.places.Place | null) => {
 *     if (place) {
 *       console.log('Selected place:', place.displayName)
 *       // Handle the selected place (e.g., update form fields)
 *     }
 *   }
 *
 *   return (
 *     <APIProvider apiKey={process.env.GOOGLE_MAPS_API_KEY}>
 *       <InputAddress
 *         placeholder="Enter an address..."
 *         onPlaceSelect={handlePlaceSelect}
 *         className="w-full"
 *       />
 *     </APIProvider>
 *   )
 * }
 * ```
 */
const InputAddress = React.forwardRef<HTMLInputElement, InputAddressProps>(
  ({ className, onPlaceSelect, ...props }, ref) => {
    const places = useMapsLibrary('places');

    const [inputValue, setInputValue] = useState<string>(
      props.defaultValue?.toString() || props.value?.toString() || ''
    );
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);
    const [isUserTyping, setIsUserTyping] = useState<boolean>(false);
    const { suggestions, resetSession } = useAutocompleteSuggestions(inputValue);
    const inputRef = useRef<HTMLInputElement>(null);

    // Sync internal state with external value prop
    useEffect(() => {
      if (props.value !== undefined) {
        setInputValue(props.value.toString());
        setIsUserTyping(false); // This is a programmatic update, not user typing
      }
    }, [props.value]);

    const isOpen = isUserTyping && inputValue.length > 0 && suggestions.length > 0;

    const handleInput = useCallback(
      (event: FormEvent<HTMLInputElement>) => {
        const value = (event.target as HTMLInputElement).value;
        setInputValue(value);
        setIsUserTyping(true); // User is actively typing
        setFocusedIndex(-1); // Reset selection when typing

        // Call the onChange prop if provided to update the form field
        if (props.onChange) {
          props.onChange(event as React.ChangeEvent<HTMLInputElement>);
        }
      },
      [props.onChange]
    );

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen || suggestions.length === 0) return;

        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            setFocusedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
            break;
          case 'ArrowUp':
            event.preventDefault();
            setFocusedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
            break;
          case 'Enter':
            event.preventDefault();
            if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
              const suggestion = suggestions[focusedIndex];
              if (suggestion) {
                handleSuggestionClick(suggestion);
              }
            }
            break;
          case 'Escape':
            event.preventDefault();
            setFocusedIndex(-1);
            setIsUserTyping(false); // Stop showing suggestions
            break;
        }
      },
      [isOpen, suggestions, focusedIndex]
    );

    const handleSuggestionClick = useCallback(
      async (suggestion: google.maps.places.AutocompleteSuggestion) => {
        if (!places) return;
        if (!suggestion.placePrediction) return;

        const place = suggestion.placePrediction.toPlace();

        await place.fetchFields({
          fields: ['addressComponents', 'formattedAddress'],
        });

        setFocusedIndex(-1);
        setIsUserTyping(false); // Stop showing suggestions after selection

        // calling fetchFields invalidates the session-token, so we now have to call
        // resetSession() so a new one gets created for further search
        resetSession();

        // Call onPlaceSelect first, which will update the form fields including streetAddress1
        onPlaceSelect(place);
      },
      [places, onPlaceSelect, resetSession]
    );

    const handleOpenChange = useCallback((open: boolean) => {
      if (!open) {
        resetSession();
        setIsUserTyping(false); // Stop showing suggestions when popover closes
      }
    }, []);

    const handleBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        // Stop showing suggestions when input loses focus
        setIsUserTyping(false);
        if (props.onBlur) {
          props.onBlur(event);
        }
      },
      [props.onBlur]
    );

    const handleFocus = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        // Don't automatically show suggestions on focus, wait for user to type
        if (props.onFocus) {
          props.onFocus(event);
        }
      },
      [props.onFocus]
    );

    // Combine refs
    React.useImperativeHandle(ref, () => inputRef.current!, []);

    return (
      <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Popover.Anchor asChild>
          <Input
            ref={inputRef}
            className={cn(className)}
            {...props}
            value={inputValue}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onFocus={handleFocus}
            autoComplete="off"
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-autocomplete="list"
          />
        </Popover.Anchor>

        <Popover.Portal>
          <Popover.Content
            className={cn(
              'z-50 w-[var(--radix-popover-trigger-width)] rounded-md border bg-popover p-0 text-popover-foreground shadow-md outline-none',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
              'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2'
            )}
            side="bottom"
            align="start"
            sideOffset={4}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="max-h-60 overflow-y-auto" role="listbox">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  role="option"
                  aria-selected={index === focusedIndex}
                  className={cn(
                    'relative flex w-full cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm outline-none',
                    'hover:bg-accent hover:text-accent-foreground',
                    'focus:bg-accent focus:text-accent-foreground',
                    'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                    index === focusedIndex && 'bg-accent text-accent-foreground',
                    index === 0 && 'rounded-t-md',
                    index === suggestions.length - 1 && 'rounded-b-md'
                  )}
                  onClick={() => handleSuggestionClick(suggestion)}
                  onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{suggestion.placePrediction?.text.text}</span>
                  </div>
                </button>
              ))}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    );
  }
);
InputAddress.displayName = 'InputAddress';

export { InputAddress };
